import { motion } from "framer-motion";
import { ArrowRight, Circle, MousePointer2, ShieldCheck, Smartphone, ShoppingCart, Type, FormInput, BarChart3, Flame, Menu } from "lucide-react";
import { useSEO } from "../hooks/useSEO";

const fadeUp = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-100px" }, transition: { duration: 0.6 } };

// ================== 13 · CRO PROCESS ==================
const auditItems = [
  { icon: Menu, label: "Navigation", note: "Findability, IA, mobile nav" },
  { icon: MousePointer2, label: "CTA", note: "Placement, copy, contrast" },
  { icon: ShieldCheck, label: "Trust", note: "Social proof, reviews, badges" },
  { icon: Smartphone, label: "Mobile UX", note: "Tap targets, load, hierarchy" },
  { icon: ShoppingCart, label: "Checkout", note: "Steps, guest, payment options" },
  { icon: Type, label: "Copy", note: "Value prop clarity" },
  { icon: FormInput, label: "Forms", note: "Fields, friction, errors" },
  { icon: BarChart3, label: "Analytics", note: "GA4 · GTM instrumentation" },
  { icon: Flame, label: "Heatmaps", note: "Scroll, click, hover patterns" },
];

const CROProcess = () => (
  <section id="cro" data-testid="cro-section" className="bg-[#F7F7F5] py-28 md:py-36">
    <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
      <motion.div {...fadeUp} className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
        <div className="lg:col-span-3 text-xs font-mono tracking-[0.2em] uppercase text-neutral-500">13 — CRO Process</div>
        <div className="lg:col-span-9">
          <h2 className="font-display text-4xl md:text-6xl leading-[1.02] tracking-tight text-neutral-900">
            The conversion audit,<br /><span className="text-neutral-400">as a consulting deliverable.</span>
          </h2>
        </div>
      </motion.div>

      {/* Browser mockup */}
      <motion.div {...fadeUp} className="rounded-2xl bg-white border border-neutral-200 overflow-hidden shadow-sm">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-neutral-200 bg-neutral-50">
          <span className="w-3 h-3 rounded-full bg-red-400" />
          <span className="w-3 h-3 rounded-full bg-yellow-400" />
          <span className="w-3 h-3 rounded-full bg-green-400" />
          <div className="mx-4 flex-1 max-w-md rounded-md bg-white border border-neutral-200 px-3 py-1 text-xs font-mono text-neutral-500">
            https://your-website.com — CRO audit
          </div>
          <div className="text-[10px] font-mono text-neutral-400">SCORE: 42/100</div>
        </div>

        <div className="p-8 md:p-12 bg-white">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
            {/* Wireframe preview */}
            <div className="lg:col-span-1 rounded-xl border-2 border-dashed border-neutral-200 bg-neutral-50 p-5 space-y-3 min-h-[380px]">
              <div className="flex items-center justify-between">
                <div className="w-16 h-3 bg-neutral-300 rounded" />
                <div className="flex gap-2">
                  <div className="w-8 h-2 bg-neutral-200 rounded" />
                  <div className="w-8 h-2 bg-neutral-200 rounded" />
                </div>
              </div>
              <div className="mt-6 w-3/4 h-6 bg-neutral-300 rounded" />
              <div className="w-full h-3 bg-neutral-200 rounded" />
              <div className="w-5/6 h-3 bg-neutral-200 rounded" />
              <div className="mt-4 inline-block px-3 py-1.5 rounded-full bg-[#3B82F6] text-white text-xs">CTA</div>
              <div className="mt-8 grid grid-cols-3 gap-2">
                <div className="h-16 bg-neutral-200 rounded" />
                <div className="h-16 bg-neutral-200 rounded" />
                <div className="h-16 bg-neutral-200 rounded" />
              </div>
            </div>

            {/* Audit cards */}
            <div className="lg:col-span-2 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {auditItems.map((a, i) => {
                const Icon = a.icon;
                return (
                  <div key={a.label} data-testid={`audit-card-${a.label.replace(/\s/g, "-").toLowerCase()}`} className="rounded-xl bg-white border border-neutral-200 p-4 hover:border-[#3B82F6] transition group">
                    <div className="flex items-center justify-between text-[10px] font-mono text-neutral-400">
                      <span>AUDIT {String(i + 1).padStart(2, "0")}</span>
                      <Circle size={6} className="fill-[#3B82F6] text-[#3B82F6]" />
                    </div>
                    <Icon size={16} className="mt-4 text-[#3B82F6]" />
                    <div className="mt-3 font-display text-lg text-neutral-900">{a.label}</div>
                    <div className="mt-1 text-xs text-neutral-500">{a.note}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="px-6 md:px-12 py-4 border-t border-neutral-200 bg-neutral-50 flex items-center justify-between text-xs font-mono text-neutral-500">
          <span>Deliverable: 40-page audit + prioritized backlog</span>
          <span>Turnaround: 7 business days</span>
        </div>
      </motion.div>
    </div>
  </section>
);

// ================== 14 · RESULTS DASHBOARD ==================
const largeKpis = [
  { v: "120+", l: "Campaigns" },
  { v: "5+", l: "Countries" },
  { v: "12", l: "Platforms" },
  { v: "8", l: "Channels" },
  { v: "6", l: "Growth Systems" },
  { v: "24/7", l: "Automation" },
];

const charts = [
  { l: "ROAS", v: "4.2x", d: "+82% YoY", color: "#3B82F6" },
  { l: "CPA", v: "-38%", d: "vs. baseline", color: "#10B981" },
  { l: "CTR", v: "3.9%", d: "industry avg 1.2%", color: "#3B82F6" },
  { l: "Conversion Rate", v: "6.4%", d: "+2.3pp", color: "#10B981" },
  { l: "Retention (D30)", v: "42%", d: "+11pp", color: "#3B82F6" },
  { l: "CAC", v: "-27%", d: "6-month trend", color: "#10B981" },
];

const ResultsDashboard = () => (
  <section id="results" data-testid="results-section" className="relative bg-[#0F1115] text-white py-28 md:py-36 overflow-hidden grain">
    <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20 relative">
      <motion.div {...fadeUp} className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
        <div className="lg:col-span-3 text-xs font-mono tracking-[0.2em] uppercase text-white/50">14 — Results</div>
        <div className="lg:col-span-9">
          <h2 className="font-display text-4xl md:text-6xl leading-[1.02] tracking-tight">
            The control room.<br /><span className="text-white/50">Seven years, one dashboard.</span>
          </h2>
        </div>
      </motion.div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {largeKpis.map((k) => (
          <div key={k.l} data-testid={`result-kpi-${k.l.replace(/\s/g, "-").toLowerCase()}`} className="glass-dark rounded-2xl p-5 hover-glow">
            <div className="font-display text-4xl md:text-5xl text-white">{k.v}</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-2">{k.l}</div>
          </div>
        ))}
      </div>

      <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {charts.map((c, i) => (
          <div key={c.l} className="glass-dark rounded-2xl p-5">
            <div className="flex items-center justify-between text-[10px] font-mono text-white/40">
              <span>METRIC · {String(i + 1).padStart(2, "0")}</span>
              <span style={{ color: c.color }}>▲</span>
            </div>
            <div className="mt-2 flex items-baseline justify-between">
              <div>
                <div className="font-display text-3xl">{c.v}</div>
                <div className="text-xs text-white/50 mt-1">{c.l}</div>
              </div>
              <div className="text-xs font-mono" style={{ color: c.color }}>{c.d}</div>
            </div>
            <svg viewBox="0 0 200 40" className="w-full h-10 mt-4">
              <defs>
                <linearGradient id={`gg${i}`} x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor={c.color} stopOpacity="0.4" />
                  <stop offset="100%" stopColor={c.color} stopOpacity="0" />
                </linearGradient>
              </defs>
              <path d={`M0,${30 - (i % 3) * 4} L25,${25 - i} L50,${20 - i} L75,${18 - i} L100,${14 - i} L125,${16 - i} L150,${8 - i} L175,${6 - i} L200,${4} L200,40 L0,40 Z`} fill={`url(#gg${i})`} />
              <path d={`M0,${30 - (i % 3) * 4} L25,${25 - i} L50,${20 - i} L75,${18 - i} L100,${14 - i} L125,${16 - i} L150,${8 - i} L175,${6 - i} L200,${4}`} fill="none" stroke={c.color} strokeWidth="1.5" />
            </svg>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ================== 15 · IF I JOIN ==================
const weeks = [
  { w: "Week 1", t: "Audit Everything", note: "Data, funnel, tracking, creative, offer" },
  { w: "Week 2", t: "Fix Tracking", note: "GA4, GTM, server-side, dashboards" },
  { w: "Week 3", t: "Optimize Conversion", note: "LP tests, offer, CTA, checkout" },
  { w: "Week 4", t: "Scale Revenue", note: "Budget shifts, automations, retention" },
];

const IfIJoin = () => (
  <section id="if-i-join" data-testid="if-i-join-section" className="bg-[#EEEDE8] py-28 md:py-36 border-t border-neutral-200">
    <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
      <motion.div {...fadeUp} className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
        <div className="lg:col-span-3 text-xs font-mono tracking-[0.2em] uppercase text-neutral-500">15 — First 30 Days</div>
        <div className="lg:col-span-9">
          <h2 className="font-display text-4xl md:text-6xl leading-[1.02] tracking-tight text-neutral-900">
            If I join your team.
          </h2>
          <p className="mt-4 text-neutral-500 max-w-2xl">A predictable, sequenced sprint that pays for itself by end of month one.</p>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {weeks.map((w, i) => (
          <div key={w.w} data-testid={`week-${i + 1}`} className="relative rounded-2xl bg-white border border-neutral-200 p-6 hover:border-[#3B82F6] transition">
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#3B82F6]">{w.w}</div>
            <div className="mt-6 font-display text-2xl text-neutral-900">{w.t}</div>
            <div className="mt-3 text-sm text-neutral-500">{w.note}</div>
            <div className="mt-8 flex items-center justify-between">
              <div className="w-8 h-8 rounded-full bg-[#0F1115] text-white text-xs flex items-center justify-center font-mono">{String(i + 1).padStart(2, "0")}</div>
              {i < 3 && <ArrowRight className="text-neutral-300" size={16} />}
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default function Approach() {
  useSEO({
    title: "The Approach · CRO Process, Results Dashboard & First 30 Days · Shivani Ranawat",
    description: "Consulting-grade CRO audit with 9 diagnostic areas, a six-year results dashboard, and a four-week onboarding sprint that pays for itself by end of month one.",
    path: "/approach",
  });
  return (
    <div data-testid="approach-page">
      <section className="pt-32 pb-16 bg-white border-b border-neutral-200">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-xs font-mono tracking-[0.2em] uppercase text-neutral-500">The Approach</div>
          <h1 className="mt-6 font-display text-5xl md:text-7xl tracking-tight text-neutral-900 max-w-4xl leading-[0.98]">
            Consulting-grade rigor.<br /> <span className="text-neutral-400">Operator-level execution.</span>
          </h1>
        </div>
      </section>

      <CROProcess />
      <ResultsDashboard />
      <IfIJoin />
    </div>
  );
}
